/**
 * UserSelector values are stored as `"<id>|<First Last>|<username>"`
 * (e.g. `"27|FMSTestManagerB Doe|FMSTestManagerB@proton.me"`).
 *
 * Three pipe-separated parts. Returns the parsed parts, or `undefined`
 * for empty values. Falls back gracefully if fewer parts are present.
 */
export const parseUserSelectorValue = (
  value: unknown
): { id: string; fullName: string; username: string } | undefined => {
  if (value == null || value === '') return undefined;
  const str = String(value);
  const parts = str.split('|');
  return {
    id: parts[0] ?? '',
    fullName: parts[1] ?? '',
    username: parts[2] ?? '',
  };
};

/**
 * Format a user selection into the wire string used by UserSelector
 * fields: `"<id>|<First Last>|<username>"`.
 */
export const formatUserSelectorValue = (user: {
  id: number | string;
  firstName?: string;
  lastName?: string;
  username: string;
}): string => {
  const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return `${user.id}|${fullName}|${user.username}`;
};

/**
 * AutoComplete values are stored as "KEY:DisplayText" (e.g. "FOR:Ford", "FOR-MUS:Mustang").
 * Returns both parts in one call.
 */
export const parseAutoCompleteValue = (
  value: unknown
): { key: string; display: string } | undefined => {
  if (value == null || value === '') return undefined;
  const str = String(value);
  const colonIndex = str.indexOf(':');
  if (colonIndex === -1) return { key: str, display: str };
  return {
    key: str.substring(0, colonIndex),
    display: str.substring(colonIndex + 1),
  };
};

/**
 * Safely resolve otherInfo to an object regardless of its runtime shape.
 * Handles: object (returned as-is), JSON string, double-encoded JSON string,
 * and legacy pipe-delimited strings (returns null so callers can fall back).
 */
export const resolveOtherInfoObj = (
  otherInfo: unknown
): Record<string, unknown> | null => {
  if (otherInfo == null) return null;
  if (typeof otherInfo === 'object')
    return otherInfo as Record<string, unknown>;
  if (typeof otherInfo !== 'string') return null;
  try {
    const parsed = JSON.parse(otherInfo);
    if (typeof parsed === 'object' && parsed !== null) return parsed;
    if (typeof parsed === 'string') {
      try {
        const inner = JSON.parse(parsed);
        if (typeof inner === 'object' && inner !== null) return inner;
      } catch {
        /* not double-encoded */
      }
    }
  } catch {
    /* not JSON — likely legacy pipe-delimited */
  }
  return null;
};

/**
 * Enrich a flat item with otherInfo field values for grid display.
 * Tries JSON object/string first, falls back to legacy pipe-split.
 */
export const enrichItemWithOtherInfo = <T extends Record<string, any>>(
  item: T,
  fieldNames: string[]
): T => {
  if (item.otherInfo == null || fieldNames.length === 0) return item;
  const newItem: any = { ...item };
  const oi = resolveOtherInfoObj(item.otherInfo);
  if (oi) {
    fieldNames.forEach((name) => {
      newItem[name] = oi[name];
    });
  } else if (typeof item.otherInfo === 'string') {
    const parts = item.otherInfo.split('|');
    fieldNames.forEach((name, i) => {
      newItem[name] = parts[i];
    });
  }
  return newItem;
};

export const IMAGE_AND_VIDEO_MEDIA_TYPE = 'imageAndVideo';

export const allowsImageAndVideo = (options: unknown): boolean => {
  if (!options || typeof options !== 'object' || Array.isArray(options)) {
    return false;
  }
  return (
    (options as { mediaType?: unknown }).mediaType ===
    IMAGE_AND_VIDEO_MEDIA_TYPE
  );
};
